import React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Command, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from "@/components/ui/command";
import {
  LayoutDashboard, Briefcase, Network, TrendingUp, Coins, Building2,
  FolderLock, ShieldCheck, Scale, BarChart3, Settings as SettingsIcon,
  FileText, UploadCloud, CheckSquare, Download, Zap, Landmark, UserCog,
} from "lucide-react";

const NAVIGATE = [
  { label: "Overview", to: "/dashboard", icon: LayoutDashboard, testid: "command-item-overview" },
  { label: "Portfolio", to: "/holdings", icon: Briefcase, testid: "command-item-portfolio" },
  { label: "Ownership graph", to: "/ownership", icon: Network, testid: "command-item-ownership" },
  { label: "Private Capital", to: "/alternatives", icon: TrendingUp, testid: "command-item-private-capital" },
  { label: "Fixed Income", to: "/fixed-income", icon: Coins, testid: "command-item-fixed-income" },
  { label: "Entities & Family", to: "/entities", icon: Building2, testid: "command-item-entities" },
  { label: "Corporate Entities", to: "/corporate", icon: Landmark, testid: "command-item-corporate" },
  { label: "Document Vault", to: "/documents", icon: FolderLock, testid: "command-item-documents" },
  { label: "Compliance", to: "/compliance", icon: ShieldCheck, testid: "command-item-compliance" },
  { label: "Governance", to: "/governance", icon: Scale, testid: "command-item-governance" },
  { label: "Reports", to: "/reports", icon: BarChart3, testid: "command-item-reports" },
  { label: "Settings", to: "/settings", icon: SettingsIcon, testid: "command-item-settings" },
  { label: "Professionals & Advisors", to: "/professionals", icon: UserCog, testid: "command-item-professionals" },
];

const QUICK_ACTIONS = [
  { label: "Manage corporate entities", to: "/corporate", icon: Landmark, testid: "command-item-corporate-manage" },
  { label: "Create board pack", to: "/reports", icon: FileText, testid: "command-item-create-board-pack" },
  { label: "Upload document", to: "/documents", icon: UploadCloud, testid: "command-item-upload-document" },
  { label: "Request approval", to: "/governance", icon: CheckSquare, testid: "command-item-request-approval" },
  { label: "Export holdings CSV", to: "/holdings", icon: Download, testid: "command-item-export-holdings" },
];

const itemClass =
  "gap-3 rounded-lg px-2.5 py-2.5 text-[color:var(--mk-text-2)] data-[selected=true]:bg-[rgba(176,141,87,0.12)] data-[selected=true]:text-[color:var(--mk-text)] aria-selected:bg-[rgba(176,141,87,0.12)] aria-selected:text-[color:var(--mk-text)]";

export const CommandPalette = ({ open, onOpenChange, onQuickActions }) => {
  const navigate = useNavigate();

  const go = (to) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="overflow-hidden border-[color:var(--mk-border-strong)] p-0 shadow-[0_18px_60px_rgba(0,0,0,0.55)] sm:max-w-[560px]"
        style={{ background: "rgba(14,21,34,0.92)", backdropFilter: "blur(20px)" }}
      >
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <DialogDescription className="sr-only">Search to navigate or run a quick action.</DialogDescription>
        <Command
          className="bg-transparent [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.18em] [&_[cmdk-group-heading]]:text-[color:var(--mk-muted)]"
        >
          <CommandInput
            placeholder="Search or jump to…"
            data-testid="command-palette-input"
            className="text-[color:var(--mk-text)]"
          />
          <CommandList className="max-h-[60vh] px-2 py-2">
            <CommandEmpty className="py-8 text-center text-sm text-[color:var(--mk-muted)]">
              No results found.
            </CommandEmpty>

            <CommandGroup heading="Navigate">
              {NAVIGATE.map((it) => (
                <CommandItem
                  key={it.to}
                  value={`nav ${it.label}`}
                  onSelect={() => go(it.to)}
                  data-testid={it.testid}
                  className={itemClass}
                >
                  <it.icon className="h-[18px] w-[18px] text-[color:var(--mk-primary)]" strokeWidth={2} />
                  <span className="text-sm">{it.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>

            <CommandSeparator className="my-2 bg-[color:var(--mk-border)]" />

            <CommandGroup heading="Quick actions">
              {onQuickActions && (
                <CommandItem
                  value="open quick actions panel"
                  onSelect={() => { onOpenChange(false); onQuickActions(); }}
                  data-testid="command-item-open-quick-actions"
                  className={itemClass}
                >
                  <Zap className="h-[18px] w-[18px] text-[color:var(--mk-primary)]" strokeWidth={2} />
                  <span className="text-sm">Open quick actions panel</span>
                  <CommandShortcut className="text-[color:var(--mk-muted)]">↵</CommandShortcut>
                </CommandItem>
              )}
              {QUICK_ACTIONS.map((it) => (
                <CommandItem
                  key={it.label}
                  value={`action ${it.label}`}
                  onSelect={() => go(it.to)}
                  data-testid={it.testid}
                  className={itemClass}
                >
                  <it.icon className="h-[18px] w-[18px] text-[color:var(--mk-text-2)]" strokeWidth={2} />
                  <span className="text-sm">{it.label}</span>
                  <CommandShortcut className="text-[color:var(--mk-muted)]">↵</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>

          <div className="flex items-center justify-between border-t border-[color:var(--mk-border)] px-3 py-2 text-[11px] text-[color:var(--mk-muted)]">
            <span>Family Souq · command</span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] px-1.5 py-0.5 font-data">↑↓</kbd>
              navigate
              <kbd className="ml-2 rounded border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] px-1.5 py-0.5 font-data">esc</kbd>
              close
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
